/**
 * GenericProjectWorkspace — /project/:id/workspace
 * P1-B: Decision Intelligence Layer
 *   - Data completeness score (Early / Developing / Advanced / Audit Ready)
 *   - Carbon pathway screening (VCM / Art6 / PoA based on data state)
 *   - Dynamic next steps panel (exact missing items with links)
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  RefreshCw, TrendingDown, DollarSign, Zap, Leaf, BarChart2,
  AlertTriangle, CheckCircle2, ArrowRight, Circle, XCircle, AlertCircle,
  Beaker, Settings, Shield,
} from "lucide-react";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";

// ── Data completeness computation ─────────────────────────────────────────
function computeCompleteness(journey, sectorCfg, evidenceCoverage) {
  const inputs   = journey?.input_snapshot || {};
  const fields   = sectorCfg?.input_fields || [];
  const baseline = journey?.baseline || {};
  const financial = journey?.financial || {};

  // Factor 1: Required field completion (40 pts)
  const requiredFields = fields.filter(f => f.required && !f.show_if);
  const filled = requiredFields.filter(f => inputs[f.key] !== undefined && inputs[f.key] !== "" && inputs[f.key] !== null);
  const fieldScore = requiredFields.length > 0 ? (filled.length / requiredFields.length) * 40 : 20;

  // Factor 2: Data tier quality (20 pts)
  const tierFields = fields.filter(f => f.tier_selector);
  const t1Count = tierFields.filter(f => inputs[`${f.key}_tier`] === "T1").length;
  const t2Count = tierFields.filter(f => inputs[`${f.key}_tier`] === "T2").length;
  const tierScore = tierFields.length > 0
    ? Math.min(20, ((t1Count * 3 + t2Count * 1.5) / (tierFields.length * 3)) * 20)
    : 0;

  // Factor 3: Baseline confirmation (20 pts)
  const bc = inputs.baseline_confirmation || baseline?.confirmation_status || "assumed_default";
  const baselineScore = { site_survey: 20, regulatory_records: 18, company_records: 15, expert_estimate: 10, assumed_default: 0 }[bc] || 0;

  // Factor 4: Evidence coverage (20 pts)
  const evidenceScore = ((evidenceCoverage?.coverage_pct || 0) / 100) * 20;

  const total = Math.min(100, Math.round(fieldScore + tierScore + baselineScore + evidenceScore));
  const level = total >= 75 ? "Data Completeness: High" : total >= 50 ? "Advanced" : total >= 25 ? "Developing" : "Early";
  const levelColor = { "Data Completeness: High": "text-emerald-700", "Advanced": "text-teal-700", "Developing": "text-amber-700", "Early": "text-red-600" };

  // Missing items
  const missing = [];
  const missingRequired = requiredFields.filter(f => !inputs[f.key]);
  if (missingRequired.length > 0) {
    missing.push({ priority: "high", icon: "input", label: `${missingRequired.length} required input${missingRequired.length > 1 ? "s" : ""} missing`, detail: missingRequired.slice(0, 2).map(f => f.label).join(", "), link: "input-data" });
  }
  if (bc === "assumed_default") {
    missing.push({ priority: "high", icon: "baseline", label: "Baseline disposal not confirmed", detail: "Site survey or regulatory records required for VVB validation", link: "input-data" });
  }
  if ((evidenceCoverage?.coverage_pct || 0) < 30) {
    missing.push({ priority: "high", icon: "evidence", label: "Critical evidence gaps", detail: `${evidenceCoverage?.coverage_pct || 0}% covered — upload key documents`, link: "evidence-vault" });
  }
  if ((t1Count + t2Count) < tierFields.length / 2 && tierFields.length > 0) {
    missing.push({ priority: "medium", icon: "tier", label: "Data sources not classified", detail: "Assign T1/T2/T3 to key fields in Input Data for confidence scoring", link: "input-data" });
  }
  if (!financial?.capex_usd || financial.capex_usd === 0) {
    missing.push({ priority: "medium", icon: "finance", label: "Project CAPEX not entered", detail: "Required for DSCR, IRR, and deal readiness calculation", link: "input-data" });
  }

  return { score: total, level, levelColor: levelColor[level], missing, t1Count, t2Count, tierTotal: tierFields.length };
}

// ── Pathway screening logic ───────────────────────────────────────────────
function computePathwayScreening(journey, completeness, evidenceCoverage) {
  const reduction = journey?.reduction || {};
  const baseline  = journey?.baseline || {};
  const annual    = Number(reduction.total_tco2e_yr || reduction.tco2e_yr || 0);
  const baselineConfirmed = (baseline.confirmation_status || "assumed_default") !== "assumed_default";
  const evPct     = evidenceCoverage?.coverage_pct || 0;
  const cScore    = completeness.score;

  const check = (met, label) => ({ met, label });
  return {
    vcm: {
      eligible: annual >= 100 && baselineConfirmed && cScore >= 25,
      label: "VCM",
      registry: "Verra VCS / Gold Standard",
      checks: [
        check(annual >= 100, `Scale: ${annual.toFixed(0)} tCO₂e/yr (min 100)`),
        check(baselineConfirmed, "Baseline confirmed by evidence"),
        check(cScore >= 25, "Data completeness ≥ Developing"),
      ],
    },
    art6: {
      eligible: annual >= 500 && baselineConfirmed && cScore >= 50 && evPct >= 30,
      label: "Article 6",
      registry: "UNFCCC Art.6.4 / National Registry",
      checks: [
        check(annual >= 500, `Scale: ${annual.toFixed(0)} tCO₂e/yr (min 500)`),
        check(baselineConfirmed, "Baseline confirmed by evidence"),
        check(cScore >= 50, "Data completeness ≥ Advanced"),
        check(evPct >= 30, `Evidence coverage ≥ 30% (${evPct}%)`),
      ],
    },
    poa: {
      eligible: annual > 0 && cScore >= 15,
      label: "PoA",
      registry: "Verra VCS PoA",
      checks: [
        check(annual > 0, "Positive annual reduction"),
        check(cScore >= 15, "Minimum data available"),
      ],
    },
    insetting: {
      eligible: annual > 0,
      label: "Insetting",
      registry: "Supply chain contractual",
      checks: [
        check(annual > 0, "Positive annual reduction"),
      ],
    },
  };
}

// ── Scope row helper (bar chart row) ────────────────────────────────────────
function ScopeRow({ label, value, color, max }) {
  const pct = max > 0 && value != null ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-slate-600">{label}</span>
        <span className="font-bold text-slate-900">{value?.toFixed ? value.toFixed(1) : (value ?? "—")} tCO₂e/yr</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
function KPI({ icon: Icon, iconBg, iconColor, value, unit, label, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center mb-3`}>
        <Icon size={16} className={iconColor} />
      </div>
      {value != null ? (
        <>
          <div className="flex items-end gap-1 mb-0.5">
            <span className="text-2xl font-bold text-slate-900 tabular-nums leading-none">{value}</span>
            {unit && <span className="text-xs text-slate-400 mb-0.5">{unit}</span>}
          </div>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </>
      ) : (
        <p className="text-sm text-slate-300 font-medium mb-0.5">Enter inputs</p>
      )}
      <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

// ── Digester Sensitivity Card ─────────────────────────────────────────────────
function DigestSensitivityCard({ journey }) {
  const base       = journey?.reduction || {};
  const sensitivity = journey?.digester_sensitivity;
  const digesterCfg = journey?.digester_configuration || "single_stage";
  const carbonBase  = (base.total_tco2e_yr || 0) * 15;   // $15/tCO₂e base
  const carbonMid   = (base.total_tco2e_yr || 0) * 22.5; // $22.5/tCO₂e mid
  const elecBase    = journey?.project_scenario?.interventions?.electricity_mwh_yr || 0;

  if (!sensitivity || Object.keys(sensitivity).length === 0) return null;

  const scenarios = [
    {
      key:   "base_case",
      label: "Base Case",
      note:  digesterCfg === "single_stage" ? "Single-stage (Active)" : "Base",
      red:   base.total_tco2e_yr || 0,
      elec:  elecBase,
      uplift: 0,
      isBase: true,
    },
    ...Object.values(sensitivity).map(s => ({
      key:    s.label,
      label:  s.label,
      note:   "Engineering uplift — methane generation only",
      red:    s.total_reduction_tco2e_yr || 0,
      elec:   s.electricity_mwh_yr || 0,
      uplift: s.uplift_pct,
      isBase: false,
    })),
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="digester-sensitivity-card">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <Settings size={13} className="text-slate-500" />
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Engineering Optimization Scenarios</p>
        <span className="text-xs text-amber-600 font-semibold ml-1">— Not Included in Base Financial Model</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="sensitivity-table">
          <thead>
            <tr className="bg-slate-800 text-white">
              {["Scenario","Emission Reduction (tCO₂e/yr)","Electricity Output (MWh/yr)","Carbon Revenue @ $15/t ($/yr)"].map(h=>(
                <th key={h} className="px-3 py-2 text-left font-semibold text-xs whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={s.key} className={`border-b border-slate-100 ${s.isBase ? "bg-teal-50" : ""}`}
                data-testid={`sensitivity-row-${i}`}>
                <td className="px-3 py-2 font-semibold text-slate-900">
                  {s.label}
                  {s.isBase && <span className="ml-2 text-teal-600 font-normal italic text-xs">Active</span>}
                  <p className="text-slate-400 font-normal text-xs">{s.note}</p>
                </td>
                <td className="px-3 py-2 font-bold text-emerald-700">{Number(s.red).toLocaleString(undefined,{minimumFractionDigits:1,maximumFractionDigits:1})}</td>
                <td className="px-3 py-2 font-bold text-blue-700">{Number(s.elec).toLocaleString(undefined,{minimumFractionDigits:1,maximumFractionDigits:1})}</td>
                <td className="px-3 py-2 font-bold text-amber-700">${(s.red * 15).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-2 bg-amber-50 border-t border-amber-100">
        <p className="text-xs text-amber-700">
          <strong>Disclaimer:</strong> Two-stage scenarios apply methane generation uplift only. Base case calculations, financial model, DSCR, and IRR are unaffected. Uplift percentages are engineering estimates pending site-specific digester design.
        </p>
      </div>
    </div>
  );
}

// ── Combined Baseline Panel ────────────────────────────────────────────────────
function CombinedBaselinePanel({ journey }) {
  const red = journey?.reduction || {};
  const s1b = red.scope1_baseline_tco2e_yr || 0;
  const s2b = red.scope2_reference_tco2e_yr || red.scope2_reference_baseline_tco2e_yr || 0;
  const comb = red.combined_baseline_tco2e_yr || 0;
  const s1r = red.scope1_reduction_tco2e_yr || red.methane_avoidance_tco2e_yr || 0;
  const s2r = red.scope2_displaced_tco2e_yr || red.electricity_displaced_tco2e_yr || 0;
  const total = red.total_tco2e_yr || 0;
  const pct   = red.combined_reduction_pct || 0;

  if (!comb) return null;

  return (
    <div className="bg-[#0F172A] rounded-xl p-5" data-testid="combined-baseline-panel">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
        Combined Baseline Reconciliation — Phase 1 Verified
      </p>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          ["S1 Baseline",  s1b,  "Methane (Scope 1)",   "text-red-400"],
          ["S2 Reference", s2b,  "Electricity (Scope 2)","text-orange-300"],
          ["Combined",     comb, "S1 + S2",              "text-white font-bold"],
        ].map(([l,v,sub,cls]) => (
          <div key={l} className="bg-slate-800/60 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-400 mb-1">{l}</p>
            <p className={`text-base ${cls}`}>{Number(v).toLocaleString(undefined,{minimumFractionDigits:1,maximumFractionDigits:1})}</p>
            <p className="text-xs text-slate-500">tCO₂e/yr</p>
            <p className="text-xs text-slate-600 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-700 pt-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-emerald-400 font-bold">
            Total Reduction: {Number(total).toLocaleString(undefined,{minimumFractionDigits:1,maximumFractionDigits:1})} tCO₂e/yr
            ({Number(s1r).toLocaleString(undefined,{maximumFractionDigits:1})} S1 + {Number(s2r).toLocaleString(undefined,{maximumFractionDigits:1})} S2)
          </p>
          <p className="text-xs text-teal-300 font-bold">{pct}% of combined baseline</p>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Validation: {Number(total).toLocaleString(undefined,{maximumFractionDigits:1})} ≤ {Number(comb).toLocaleString(undefined,{maximumFractionDigits:1})} ✓ — mathematical requirement satisfied
        </p>
      </div>
    </div>
  );
}
function DataCompletenessPanel({ completeness, navigate, id }) {
  const levelBg = {
    "Data Completeness: High": "bg-emerald-50 border-emerald-200",
    "Advanced": "bg-teal-50 border-teal-200",
    "Developing": "bg-amber-50 border-amber-200",
    "Early": "bg-red-50 border-red-200",
  };
  const barColor = {
    "Data Completeness: High": "bg-emerald-500", "Advanced": "bg-teal-500",
    "Developing": "bg-amber-500", "Early": "bg-red-400",
  };
  return (
    <div className={`border rounded-xl p-5 ${levelBg[completeness.level]}`} data-testid="data-completeness-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Data Completeness</p>
        <span className={`text-sm font-bold ${completeness.levelColor}`}>{completeness.level}</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-2.5 bg-white/60 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${barColor[completeness.level]}`}
            style={{ width: `${completeness.score}%` }} />
        </div>
        <span className={`text-xs font-bold flex-shrink-0 ${completeness.levelColor}`}>{completeness.score}/100</span>
      </div>
      {/* Tier breakdown */}
      {completeness.tierTotal > 0 && (
        <div className="flex gap-2 mb-3 text-xs">
          <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">T1: {completeness.t1Count}</span>
          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">T2: {completeness.t2Count}</span>
          <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-semibold">T3: {completeness.tierTotal - completeness.t1Count - completeness.t2Count}</span>
          <span className="text-slate-400 italic">of {completeness.tierTotal} tracked fields</span>
        </div>
      )}
      {/* Missing items */}
      {completeness.missing.length > 0 && (
        <div className="space-y-1.5">
          {completeness.missing.slice(0, 4).map((item, i) => (
            <button key={i} onClick={() => navigate(`/project/${id}/${item.link}`)}
              className="w-full flex items-start gap-2 text-left hover:bg-white/40 rounded-lg px-2 py-1.5 transition-colors"
              data-testid={`completeness-item-${i}`}>
              <AlertTriangle size={10} className={`flex-shrink-0 mt-0.5 ${item.priority === "high" ? "text-red-500" : "text-amber-500"}`} />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-700">{item.label}</p>
                <p className="text-xs text-slate-500 truncate">{item.detail}</p>
              </div>
              <ArrowRight size={9} className="text-slate-400 flex-shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      )}
      {completeness.missing.length === 0 && (
        <div className="flex items-center gap-1.5">
          <CheckCircle2 size={12} className="text-emerald-500" />
          <p className="text-xs text-emerald-700 font-semibold">All key requirements met — audit ready</p>
        </div>
      )}
    </div>
  );
}

// ── Pathway Screening Panel ────────────────────────────────────────────────
function PathwayScreeningPanel({ pathwayScreening, navigate, id }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="pathway-screening">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Carbon Pathway Screening</p>
      {Object.values(pathwayScreening).map(pathway => (
        <div key={pathway.label} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b last:border-0 border-slate-50">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-slate-700">{pathway.label}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
              pathway.eligible
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }`}>{pathway.eligible ? "Structurally Aligned" : "Conditions Required"}</span>
          </div>
          <div className="space-y-0.5">
            {pathway.checks.map((check, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {check.met
                  ? <CheckCircle2 size={9} className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={9} className="text-slate-300 flex-shrink-0" />}
                <span className={`text-xs ${check.met ? "text-slate-500" : "text-slate-400"}`}>{check.label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={() => navigate(`/project/${id}/carbon-strategy`)}
        className="mt-3 w-full text-xs text-teal-600 font-semibold hover:underline text-center">
        Full Strategy →
      </button>
    </div>
  );
}

// ── Dynamic Next Steps Panel ───────────────────────────────────────────────
function NextStepsPanel({ completeness, navigate, id, isEmpty }) {
  const steps = completeness.missing.length > 0
    ? completeness.missing
    : [
        { priority: "ok", label: "Review Climate Snapshot", detail: "Emissions baseline and reduction", link: "climate-snapshot" },
        { priority: "ok", label: "Upload Evidence Documents", detail: "VVB requires physical evidence", link: "evidence-vault" },
        { priority: "ok", label: "Review Financing Model", detail: "DSCR and deal pathways", link: "financing" },
        { priority: "ok", label: "Carbon Strategy", detail: "Pathway selection and revenue", link: "carbon-strategy" },
      ];

  return (
    <div className="bg-[#0F172A] rounded-xl p-5">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
        {completeness.missing.length > 0 ? "Action Required" : "Next Steps"}
      </p>
      <div className="space-y-1.5">
        {steps.slice(0, 5).map(({ priority, label, detail, link }, i) => (
          <button key={i} onClick={() => navigate(`/project/${id}/${link}`)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-left transition-colors">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              priority === "high" ? "bg-red-400" : priority === "medium" ? "bg-amber-400" : "bg-teal-400"
            }`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-white font-semibold truncate">{label}</p>
              <p className="text-xs text-slate-500 truncate">{detail}</p>
            </div>
            <ArrowRight size={10} className="text-slate-500 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────
function EmptyState({ sector, sectorLabel, id, navigate }) {
  const icons = { biogas: Zap, solar: Zap, industrial: BarChart2, transport: TrendingDown, agriculture: Leaf };
  const Icon = icons[sector] || TrendingDown;
  const hints = {
    biogas:     "Feedstock input, waste type, biogas yield, capture efficiency, CAPEX",
    solar:      "Installed capacity, baseline consumption, grid factor, CAPEX",
    industrial: "Baseline and project fuel + electricity consumption, CAPEX",
    transport:  "Fleet size, baseline and project fuel consumption, CAPEX",
    agriculture:"Land area, crop type, practice intervention, CAPEX",
  };
  return (
    <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center" data-testid="empty-state">
      <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4">
        <Icon size={24} className="text-teal-600" />
      </div>
      <h2 className="text-lg font-bold text-slate-900 mb-2">{sectorLabel} — No Calculation Yet</h2>
      <p className="text-sm text-slate-500 mb-1">Enter project inputs to generate your first assessment.</p>
      <p className="text-xs text-slate-400 mb-6 max-w-xs mx-auto">Key inputs needed: {hints[sector] || "project-specific inputs"}.</p>
      <button onClick={() => navigate(`/project/${id}/input-data`)}
        className="flex items-center gap-2 px-6 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 mx-auto"
        data-testid="enter-inputs-cta">
        Enter Project Inputs <ArrowRight size={14} />
      </button>
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function GenericProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [poolStatus, setPoolStatus] = React.useState(null);
  const {
    loading, isEmpty, journey, reduction, baseline, projectScenario,
    financial, kpis, org, sector, sectorLabel, methodology, protocol,
    calcVersion, coBenefits, evidenceCoverage, sectorCfg, projectState,
    projectStage, projectStageLabel,
    vrsScore, vrsBand,
    scopeOutputs,
    verificationStatus, verificationStatusLabel, verificationVvbLabel,
  } = useProjectWorkspace(id);

  const investorStatus = journey?.investor_status;

  React.useEffect(() => {
    if (!id) return;
    import("@/api/client").then(({ default: client }) => {
      client.get(`/workspace/poa/project/${id}/status`)
        .then(r => setPoolStatus(r.data))
        .catch(() => {});
    });
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  // P1-B computations — use CDE (project_state) when available, local functions as fallback
  const cdeBacked = !!projectState;

  // ── VRS — from canonical backend (no client recomputation) ───────────────
  // vrsScore and vrsBand come from useProjectWorkspace hook (project_state.vrs)
  const wsVrsBandColor = vrsBand === "Pre-Verification Ready" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : vrsBand === "Validation-Ready" ? "bg-teal-100 text-teal-800 border-teal-300"
    : vrsBand === "Developing"       ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-slate-100 text-slate-500 border-slate-200";

  const completeness = cdeBacked ? {
    score: projectState.completeness.score,
    level: projectState.completeness.level,
    levelColor: { "Data Completeness: High":"text-emerald-700","Advanced":"text-teal-700","Developing":"text-amber-700","Early":"text-red-600" }[projectState.completeness.level] || "text-slate-600",
    t1Count: 0, t2Count: 0, tierTotal: 0,
    missing: projectState.overall.warnings.map(w => ({
      priority: w.level === "critical" ? "high" : w.level,
      label: w.message.length > 60 ? w.message.slice(0, 57) + "..." : w.message,
      detail: w.message,
      link: w.code?.includes("EVIDENCE") || w.code?.includes("BASELINE") ? "evidence-vault" : "input-data",
    })),
  } : computeCompleteness(journey, sectorCfg, evidenceCoverage);

  const pathwayScreening = cdeBacked ? {
    vcm:      { ...projectState.carbon_pathway.vcm,       label:"VCM",      registry:"Verra VCS",     checks:(projectState.carbon_pathway.vcm.issues||[]).map(i=>({met:false,label:i})) },
    art6:     { ...projectState.carbon_pathway.art6,      label:"Article 6", registry:"UNFCCC Art.6", checks:(projectState.carbon_pathway.art6.issues||[]).map(i=>({met:false,label:i})) },
    poa:      { ...projectState.carbon_pathway.poa,       label:"PoA",      registry:"Verra VCS PoA", checks:(projectState.carbon_pathway.poa.issues||[]).map(i=>({met:false,label:i})) },
    insetting:{ ...projectState.carbon_pathway.insetting, label:"Insetting", registry:"Supply chain", checks:(projectState.carbon_pathway.insetting.issues||[]).map(i=>({met:false,label:i})) },
  } : computePathwayScreening(journey, completeness, evidenceCoverage);

  // effectiveLevel uses CDE directly — no need for local fallback gate since CDE enforces evidence
  const effectiveLevel = completeness;

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="generic-workspace">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
          <PageHeader title={journey?.name || sectorLabel || "Project"} />
          <p className="text-xs text-slate-400 mt-0.5">
            {sectorLabel} · {protocol || methodology} · {org.country || "—"}
            {calcVersion && <span className="ml-2">· v{calcVersion.number} · {calcVersion.hash?.slice(0,10)}...</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 3 header badges — VRS / Verification Status / Data Quality */}
          {!isEmpty && vrsScore != null && vrsScore > 0 && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${wsVrsBandColor}`}
              data-testid="workspace-vrs-badge" title={`Verification Readiness Score: ${vrsScore}/100`}>
              VRS {vrsScore} — {vrsBand}
            </span>
          )}
          {!isEmpty && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
              verificationStatus === "IN_VALIDATION"
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }`} data-testid="verification-status-badge"
              title="Verification status — independent VVB engagement required for formal verification">
              {verificationStatus === "IN_VALIDATION"
                ? "Verification: In Progress"
                : "Verification: Not Initiated"}
            </span>
          )}
          {!isEmpty && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
              effectiveLevel.level === "Data Completeness: High" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
              effectiveLevel.level === "Advanced"    ? "bg-teal-100 text-teal-700 border-teal-200" :
              effectiveLevel.level === "Developing"  ? "bg-amber-100 text-amber-700 border-amber-200" :
                                                       "bg-red-50 text-red-600 border-red-200"
            }`} data-testid="completeness-badge">
              Data Completeness: {effectiveLevel.level === "Data Completeness: High" ? `High (${effectiveLevel.score}%)` : `${effectiveLevel.level} (${effectiveLevel.score}%)`}
            </span>
          )}
        </div>
      </div>

      {/* Status banner — aligned with global project stage */}
      {!isEmpty && (() => {
        const SDSP = {
          finance_ready:        { label: "Financial Model: Structurally Viable (Validation-Ready)", sub: "DSCR threshold met. Subject to independent technical and financial due diligence.", color: "bg-teal-50 border-teal-300",  dot: "bg-teal-500",  icon: "✓" },
          restructure_required: { label: "Restructuring Required",                sub: "DSCR below 1.2x threshold. Review Financing for restructuring options.",    color: "bg-amber-50 border-amber-300", dot: "bg-amber-500", icon: "!" },
          do_not_present:       { label: "NOT FINANCEABLE — Restructuring Required", sub: "DSCR <1.0x with no viable pathway. Restructuring required before any lender engagement.", color: "bg-red-50 border-red-400", dot: "bg-red-500", icon: "!" },
          early_stage:          { label: "Early Stage — Model Initializing",  sub: "Continue building evidence and completing inputs.",               color: "bg-slate-50 border-slate-300", dot: "bg-slate-400", icon: "~" },
        };
        const disp = SDSP[investorStatus] || SDSP.early_stage;
        return (
          <div className={`border rounded-xl px-5 py-3.5 mb-5 flex items-center gap-3 ${disp.color}`} data-testid="readiness-banner">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${disp.dot}`}>
              <span className="text-white text-xs font-bold">{disp.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700">{disp.label}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{disp.sub}</p>
            </div>
          </div>
        );
      })()}

      {isEmpty && <EmptyState sector={sector} sectorLabel={sectorLabel} id={id} navigate={navigate} />}

      {!isEmpty && (
        <>
          {/* KPI strip — from canonical scope_outputs for consistency */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPI icon={TrendingDown} iconBg="bg-teal-50" iconColor="text-teal-600"
              value={scopeOutputs?.net_total_reduction_tco2e_yr > 0
                ? `~${Number(scopeOutputs.net_total_reduction_tco2e_yr).toFixed(1)}`
                : (reduction?.total_tco2e_yr > 0 ? `~${Number(reduction.total_tco2e_yr).toFixed(1)}` : null)}
              unit="tCO₂e/yr" label="Net Reduction (S1+S2)"
              sub={scopeOutputs?.total_reduction_pct
                ? `${scopeOutputs.total_reduction_pct}% of total baseline (S1+S2)`
                : `${reduction?.reduction_pct?.toFixed?.(1) || "—"}% Scope 1 only`} />
            <KPI icon={BarChart2} iconBg="bg-blue-50" iconColor="text-blue-600"
              value={reduction?.lifetime_tco2e > 0 ? `~${Number(reduction.lifetime_tco2e).toLocaleString()}` : null}
              unit="tCO₂e" label="Lifetime Impact" sub={`${reduction?.project_life_years || "—"}yr`} />
            <KPI icon={DollarSign} iconBg="bg-emerald-50" iconColor="text-emerald-600"
              value={financial?.cost_per_tco2e ? `$${financial.cost_per_tco2e}` : null}
              label="Cost per tCO₂e" sub="Lifetime basis" />
            <KPI icon={DollarSign} iconBg="bg-amber-50" iconColor="text-amber-600"
              value={financial?.annual_savings_usd_yr && financial.capex_usd > 0 ? `$${Number(financial.annual_savings_usd_yr).toLocaleString()}` : null}
              unit="/yr" label="Annual Savings" sub={financial?.annual_savings_type?.replace(/_/g," ") || ""} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left 2/3 */}
            <div className="lg:col-span-2 space-y-5">
              {/* Emission structure — scope-by-scope (reads from canonical scope_outputs) */}
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="emission-comparison">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Emission Structure — Scope by Scope</p>
                  <span className="text-xs text-slate-400 italic">Baseline / Project / Reduction</span>
                </div>
                {(() => {
                  const so = scopeOutputs;
                  if (!so) return (
                    <p className="text-xs text-slate-400 italic">Run a calculation to generate scope breakdown.</p>
                  );
                  const s1 = so.scope_1 || {};
                  const s2 = so.scope_2 || {};
                  const totalBase = so.total_baseline_tco2e_yr || 0;
                  const totalRed  = so.net_total_reduction_tco2e_yr || 0;
                  const totalPct  = so.total_reduction_pct || 0;
                  const Row = ScopeRow;

                  return (
                    <div className="space-y-4">
                      {/* Scope 1 */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-xs font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">S1</span>
                          <span className="text-xs font-bold text-slate-600 uppercase">Waste Methane Boundary</span>
                        </div>
                        <Row label="Baseline" value={s1.baseline_tco2e_yr}  color="bg-red-400"     max={s1.baseline_tco2e_yr || 1} />
                        <Row label="Project"  value={s1.project_tco2e_yr}   color="bg-teal-500"    max={s1.baseline_tco2e_yr || 1} />
                        <Row label="Reduction"value={s1.reduction_tco2e_yr} color="bg-emerald-500" max={s1.baseline_tco2e_yr || 1} />
                      </div>
                      {/* Scope 2 */}
                      {s2.baseline_tco2e_yr > 0 && (
                        <div className="pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">S2</span>
                            <span className="text-xs font-bold text-slate-600 uppercase">Energy Boundary</span>
                            <span className="text-xs text-amber-600 italic ml-1">({(s2.baseline_source || "").split("—")[0].trim()})</span>
                          </div>
                          <Row label="Baseline" value={s2.baseline_tco2e_yr}  color="bg-red-300"  max={s2.baseline_tco2e_yr || 1} />
                          <Row label="Project"  value={s2.project_tco2e_yr}   color="bg-teal-400" max={s2.baseline_tco2e_yr || 1} />
                          <Row label="Reduction"value={s2.reduction_tco2e_yr} color="bg-blue-400" max={s2.baseline_tco2e_yr || 1} />
                          <p className="text-xs text-slate-400 italic mt-1">Project = 0 — biogas replaces fossil source</p>
                        </div>
                      )}
                      {/* Total */}
                      <div className="pt-3 border-t-2 border-slate-200 flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <TrendingDown size={15} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">
                            {Number(totalRed).toLocaleString()} tCO₂e/yr net — {totalPct}% of total baseline ({Number(totalBase).toLocaleString()} tCO₂e/yr)
                          </p>
                          <p className="text-xs text-slate-400">
                            Baseline {Number(totalBase).toFixed(1)} − Project {Number(so.total_project_tco2e_yr || 0).toFixed(1)} = {Number(totalRed).toFixed(1)} ✓
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500 leading-relaxed">{baseline?.scenario}</p>
                  {baseline?.audit_flag && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <AlertTriangle size={11} className="text-amber-500" />
                      <p className="text-xs text-amber-600 font-semibold">Baseline: Assumed default — confirm via site survey before VVB engagement</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Project scenario */}
              <div className="bg-[#0F172A] rounded-xl px-5 py-4">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5">Project Scenario</p>
                <p className="text-xs text-white leading-snug">{projectScenario?.scenario}</p>
                {reduction?.intensity_note && (
                  <p className="text-xs text-teal-400 mt-2">
                    Intensity: {reduction.intensity_tco2e_per_tonne || reduction.intensity_tco2e_per_kwp || "—"} · {reduction.intensity_note}
                  </p>
                )}
              </div>

              {/* Combined Baseline Reconciliation Panel */}
              <CombinedBaselinePanel journey={journey} />

              {/* Digester Sensitivity Analysis */}
              <DigestSensitivityCard journey={journey} />

              {/* Data completeness panel (P1-B) */}
              <DataCompletenessPanel completeness={effectiveLevel} navigate={navigate} id={id} />

              {/* Co-benefits */}
              {coBenefits && Object.keys(coBenefits).length > 0 && (
                <SectionCard>
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Co-Benefits</p>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(coBenefits)
                      .filter(([, v]) => v && typeof v !== "string" && Number(v) > 0)
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <div key={k} className="bg-slate-50 rounded-xl p-3">
                          <p className="text-xs text-slate-400 mb-0.5">{k.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                          <p className="text-sm font-semibold text-slate-900">
                            {typeof v === "number" ? v.toLocaleString() : v}
                          </p>
                        </div>
                      ))}
                  </div>
                </SectionCard>
              )}
            </div>

            {/* Right 1/3 */}
            <div className="space-y-4">
              {/* Pathway Screening (P1-B) */}
              <PathwayScreeningPanel pathwayScreening={pathwayScreening} navigate={navigate} id={id} />

              {/* Financial summary */}
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="financial-summary">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Financial Summary</p>
                {[
                  ["CAPEX",         financial?.capex_usd > 0 ? `$${Number(financial.capex_usd).toLocaleString()}` : "—", "text-slate-900"],
                  ["Annual Savings", financial?.annual_savings_usd_yr && financial?.capex_usd > 0 ? `$${Number(financial.annual_savings_usd_yr).toLocaleString()}/yr` : "—", "text-emerald-700"],
                  ["Payback",       financial?.simple_payback_years && financial?.capex_usd > 0 ? `${financial.simple_payback_years} yr` : "—", "text-teal-700"],
                  ["Cost/tCO₂e",   financial?.cost_per_tco2e && financial?.capex_usd > 0 ? `$${financial.cost_per_tco2e}` : "—", "text-teal-700"],
                ].map(([l, v, c]) => (
                  <div key={l} className="flex justify-between items-start py-1.5 border-b border-slate-50 last:border-0">
                    <p className="text-xs text-slate-500">{l}</p>
                    <span className={`text-xs font-bold ${c}`}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Dynamic Next Steps (P1-B) */}
              <NextStepsPanel completeness={effectiveLevel} navigate={navigate} id={id} isEmpty={isEmpty} />

              {/* PoA status */}
              {poolStatus?.registered && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="poa-pool-card">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Programme Aggregation (PoA)</p>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-900 truncate">{poolStatus.pool_ref_id}</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200">
                      {(poolStatus.pool_status_label || poolStatus.pool_status || "—").split(" —")[0]}
                    </span>
                  </div>
                  {[
                    ["Pool size", `${(poolStatus.pool_total_reduction_tco2e||0).toLocaleString()} tCO₂e/yr`],
                    ["Projects",  poolStatus.pool_project_count],
                    ["Your share",`${poolStatus.your_share_pct || 0}%`],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-xs py-0.5">
                      <span className="text-slate-400">{l}</span>
                      <span className="font-semibold text-slate-700">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {journey?.disclosure_note && (
            <p className="text-xs text-slate-400 italic text-center mt-5 pb-2">{journey.disclosure_note}</p>
          )}
        </>
      )}
    </div>
  );
}

